import { AssetClass } from '../types/assetClass';
import { CorrelationMatrix, AssetType } from '../types/correlationMatrix';
import { SimulationResult, Allocation } from '../types/simulation';

function generateRandomNormal(): number {
  // Box-Muller transform
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

function choleskyDecomposition(matrix: number[][]): number[][] {
  const n = matrix.length;
  const L = Array(n).fill(0).map(() => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = 0;
      for (let k = 0; k < j; k++) {
        sum += L[i][k] * L[j][k];
      }
      if (i === j) {
        L[i][i] = Math.sqrt(Math.max(matrix[i][i] - sum, 0));
      } else {
        L[i][j] = (matrix[i][j] - sum) / L[j][j];
      }
    }
  }
  return L;
}

function generateCorrelatedReturns(
  means: number[],
  volatilities: number[],
  correlationMatrix: number[][],
  numSamples: number
): number[][] {
  const n = means.length;
  const L = choleskyDecomposition(correlationMatrix);
  const uncorrelated = Array(numSamples).fill(0).map(() => 
    Array(n).fill(0).map(() => generateRandomNormal())
  );
  
  return uncorrelated.map(sample => {
    const correlated = Array(n).fill(0);
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let j = 0; j <= i; j++) {
        sum += L[i][j] * sample[j];
      }
      // Convert to return using mean and volatility
      correlated[i] = means[i] / 100 + (volatilities[i] / 100) * sum;
    }
    return correlated;
  });
}

export function simulatePortfolio(
  assetClasses: AssetClass[],
  allocation: Partial<Allocation>,
  correlationMatrix: CorrelationMatrix,
  initialValue: number,
  years: number,
  iterations: number = 1000,
  monthlyContribution: number = 0
): SimulationResult {
  // Setup parameters
  const timePoints = Array.from({ length: years + 1 }, (_, i) => i);
  const stepsPerYear = 12;
  const totalSteps = years * stepsPerYear;
  
  // Extract parameters for simulation
  const orderedAssets = assetClasses.map(ac => ac.name.toLowerCase());
  const means = orderedAssets.map(name => assetClasses.find(ac => 
    ac.name.toLowerCase() === name)!.longTermReturn);
  const vols = orderedAssets.map(name => assetClasses.find(ac => 
    ac.name.toLowerCase() === name)!.volatility);
  const weights = orderedAssets.map(name => (allocation[name.toLowerCase() as keyof Allocation] || 0) / 100);
  
  // Create correlation matrix in same order as assets
  const corrMatrix = orderedAssets.map(asset1 => 
    orderedAssets.map(asset2 => {
      const key1 = asset1 as AssetType;
      const key2 = asset2 as AssetType;
      return correlationMatrix.correlations[key1][key2];
    })
  );

  // Scale parameters to monthly
  const monthlyMeans = means.map(r => r / 12);
  const monthlyVols = vols.map(v => v / Math.sqrt(12));

  // Generate correlated monthly returns
  const monthlyReturns = generateCorrelatedReturns(
    monthlyMeans,
    monthlyVols,
    corrMatrix,
    totalSteps * iterations
  );

  // Calculate portfolio values
  const values: number[][] = Array(iterations).fill(0).map(() => [initialValue]);
  
  for (let iter = 0; iter < iterations; iter++) {
    let value = initialValue;
    for (let step = 0; step < totalSteps; step++) {
      const monthReturns = monthlyReturns[iter * totalSteps + step];
      const portfolioReturn = monthReturns.reduce(
        (sum, ret, i) => sum + ret * weights[i],
        0
      );
      // Add monthly contribution and apply return
      value = (value + monthlyContribution) * (1 + portfolioReturn);
      
      if ((step + 1) % stepsPerYear === 0) {
        values[iter][(step + 1) / stepsPerYear] = value;
      }
    }
  }

  // Calculate percentiles for each time point
  const percentiles = {
    p95: Array(years + 1).fill(0),
    p75: Array(years + 1).fill(0),
    p50: Array(years + 1).fill(0),
    p25: Array(years + 1).fill(0),
    p05: Array(years + 1).fill(0),
  };

  for (let t = 0; t <= years; t++) {
    const valuesAtTime = values.map(path => path[t]).sort((a, b) => a - b);
    percentiles.p95[t] = valuesAtTime[Math.floor(0.95 * iterations)];
    percentiles.p75[t] = valuesAtTime[Math.floor(0.75 * iterations)];
    percentiles.p50[t] = valuesAtTime[Math.floor(0.50 * iterations)];
    percentiles.p25[t] = valuesAtTime[Math.floor(0.25 * iterations)];
    percentiles.p05[t] = valuesAtTime[Math.floor(0.05 * iterations)];
  }

  return {
    time: timePoints,
    values,
    percentiles,
  };
}
